import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import {
  BaseText,
  BottomButtonsLayout,
  Button,
  Headline1,
  Headline3,
  Meta,
} from 'aimo-ui';
import CloseButton from 'components/common/CloseButton';
import Info from 'components/common/Info';
import Icon from 'components/icons/Icon';
import SvgImage from 'components/svgImage/SvgImage';
import LongTermParkingContext from 'context/LongTermParkingContext';
import SafeAreaHeaderContainer from 'navigation/SafeAreaHeaderContainer';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import styled from 'styled-components/native';
import { ColumnWithMargins, ContainerWithGap } from 'styles/ContainerStyles';
import { LongTermStackParamList, RootNavProps } from 'types/navigation';
import { isWeb } from 'utils/deviceUtils';

const Title = styled(Headline1)`
  font-size: 32px;
`;

const AlignedIcon = styled(Icon)`
  transform: translateY(5px) translateX(-2px);
`;

const WaitingListCompletedScreen: React.FC = () => {
  const { t } = useTranslation();
  const { replace } = useNavigation<RootNavProps>();

  const handleCompleted = () => {
    replace('permits', {});
  };

  const { params: { pmcId } = { pmcId: '' } } =
    useRoute<RouteProp<LongTermStackParamList, 'waitingListCompleted'>>();

  const { selectedPmcs } = useContext(LongTermParkingContext);
  const pmc = selectedPmcs?.find((p) => p.pmcId === pmcId);

  const waitingListActivationDays =
    (pmc?.expirationLimits?.waitingListActivationPermitLock ?? 0) / 60 / 24;

  return (
    <BottomButtonsLayout
      contentBottom={
        <ColumnWithMargins mb={isWeb ? 40 : 0}>
          <Button size="l" text={t('button.done')} onPress={handleCompleted} />
        </ColumnWithMargins>
      }>
      <ContainerWithGap>
        <SafeAreaHeaderContainer
          rightContent={
            <ColumnWithMargins mr={20}>
              <CloseButton
                testID="long-term-parking-complete-close-button"
                onPress={handleCompleted}
                buttonType="secondary"
              />
            </ColumnWithMargins>
          }
        />
        <ColumnWithMargins mt={40}>
          <SvgImage height={200} width={200} name="OnboardingComplete" />
        </ColumnWithMargins>
        <Title center>{t('longTermParking.complete.thankYou')}</Title>
        <BaseText center>
          {t('longTermParking.waitingList.completed.created')}
        </BaseText>
        <ColumnWithMargins mt={20}>
          <Headline3>
            {t('longTermParking.waitingList.completed.next')}
          </Headline3>
        </ColumnWithMargins>
        <View>
          <Info
            last={false}
            text={t('longTermParking.waitingList.info.step1')}
            icon={'Notification-Outlined'}
            separator={2}
          />
          <Info
            last
            text={t('longTermParking.waitingList.info.step2', {
              count: waitingListActivationDays,
            })}
            icon={'check'}
          />
          <ColumnWithMargins ml={42}>
            <Meta>
              {!isWeb && <AlignedIcon size={'s'} name={'Info-Outlined'} />}
              {t('longTermParking.waitingList.completed.description')}
            </Meta>
          </ColumnWithMargins>
        </View>
      </ContainerWithGap>
    </BottomButtonsLayout>
  );
};

export default WaitingListCompletedScreen;
